import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface ScrollToTopProps {
  behavior?: 'auto' | 'instant';
  delay?: number;
}

const ScrollToTop: React.FC<ScrollToTopProps> = ({
  behavior = 'instant',
  delay = 0
}) => {
  const { pathname, hash } = useLocation();
  const previousPathname = useRef<string>('');

  useEffect(() => {
    const scrollToTop = () => {
      // Don't scroll if there's a hash (anchor link) or path hasn't changed
      if (hash || previousPathname.current === pathname) {
        return;
      }

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        // Single, efficient scroll reset
        if (behavior === 'instant') {
          // Instant scroll for better performance
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        } else {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
          });
        }
      });

      // Update previous pathname
      previousPathname.current = pathname;
    };

    if (delay > 0) {
      const timer = setTimeout(scrollToTop, delay);
      return () => clearTimeout(timer);
    } else {
      scrollToTop();
    }
  }, [pathname, hash, behavior, delay]);

  return null;
};

export default ScrollToTop;